// Generated by Copilot- Start
/**
 * ============================================================================
 * CATEGORIES.TS - Category management and search index
 * ============================================================================
 *
 * This file consolidates all category and search index operations:
 * - Category lookups and filtering
 * - SVOD label enhancement
 * - Search index building
 *
 * Replaces: categories.ts, data-extraction.ts, search-index.ts
 */

import { KeyValuePair } from "@directv-digital/dds.services.channel-lineup-provider";
import { ChannelCardV2, SearchIndexEntry } from "./types";
import { resolveGenreSvodNameByCode } from "../svod-utils";

// ============================================================================
// SECTION 1: CATEGORY OPERATIONS
// ============================================================================

export const buildCategoryLookup = (
  categories: KeyValuePair[] | undefined
): Record<string, KeyValuePair> => {
  if (!categories?.length) {
    return {};
  }

  return categories.reduce((acc, category) => {
    if (category?.key) {
      acc[category.key] = category;
    }
    return acc;
  }, {} as Record<string, KeyValuePair>);
};

export const filterCategories = (
  categories: KeyValuePair[] | undefined,
  suppressCategory: string[] | undefined
): KeyValuePair[] => {
  if (!categories?.length) {
    return [];
  }
  if (!suppressCategory?.length) {
    return categories;
  }
  const suppressed = new Set(suppressCategory);
  return categories.filter(category => !suppressed.has(category.key));
};

export const formatSvodArrayToString = (arr: string[]): string => {
  if (!arr || arr.length === 0) {
    return "";
  }
  if (arr.length === 1) {
    return arr[0];
  }
  if (arr.length === 2) {
    return `${arr[0]} and ${arr[1]}`;
  }

  const allButLast = arr.slice(0, -1);
  const lastItem = arr[arr.length - 1];
  return `${allButLast.join(", ")} and ${lastItem}`;
};

interface EnhanceCategoriesArgs {
  categories: KeyValuePair[];
  svodGenreData?: any[];
  svodMapping?: Record<string, string[]>;
  // WAT-11085: EPOCH genreSvodOffers.svodNameMapping (source of truth) + embedded-svods
  // feed, so the label name comes from EPOCH — not the shop PCP-proxy svodGenreData name.
  svodNameMapping?: Record<string, string>;
  embeddedSvods?: any[];
}

export const enhanceCategoriesWithSvodLabels = ({
  categories,
  svodGenreData,
  svodMapping,
  svodNameMapping,
  embeddedSvods
}: EnhanceCategoriesArgs): KeyValuePair[] => {
  if (!svodGenreData || !svodMapping) {
    return categories;
  }

  return categories.map(({ key, value }) => {
    // For genre variant: key is already ATT_TV_MYSPORTS (from packageConfig)
    // For standard variant: key is SPORTS, ENTERTAINMENT, etc. (from CMS channelCategories)
    // Use key directly - svodMapping always uses full IDs like ATT_TV_MYSPORTS
    //
    // WAT-11085: svodGenreData (shop PCP-proxy feed) is used only for membership/order.
    // The displayed NAME is resolved from EPOCH via resolveGenreSvodNameByCode — bundled
    // by code (this is a text-only label, so no split Disney+/Hulu here).
    const svodNames = formatSvodArrayToString(
      svodGenreData
        .filter(svodPackage => svodMapping[key]?.includes(svodPackage?.productSKU))
        .map(svodPackage => resolveGenreSvodNameByCode(svodPackage?.productSKU, {
          svodNameMapping,
          embeddedSvods,
          // EPOCH miss must never leak a raw BOLT-… SKU to the category label —
          // fall back to the human-readable shop name (membership source here anyway).
          fallbackName: svodPackage?.name
        }))
        .filter(Boolean) || []
    );

    const newValue = svodNames ? `${value} (with ${svodNames})` : value;

    return {
      key,
      value: newValue
    };
  });
};

// ============================================================================
// SECTION 2: SEARCH INDEX
// ============================================================================

export const buildSearchIndex = (
  visible: ChannelCardV2[],
  hidden: Record<string, { channels: ChannelCardV2[] }>
): SearchIndexEntry[] => {
  const visibleEntries = visible.map(channel => ({
    id: channel.id,
    label: channel.label,
    categoryId: channel.categoryId,
    isHidden: false
  }));

  const hiddenEntries = Object.values(hidden || {}).flatMap(group => (group.channels || []).map(channel => ({
    id: channel.id,
    label: channel.label,
    categoryId: channel.categoryId,
    isHidden: true
  })));

  return [...visibleEntries, ...hiddenEntries];
};
// Generated by Copilot- End
